import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Tag from "@/components/base/Tag";
import { guideCategories, guides } from "@/mocks/guides";
import { siteInfo } from "@/mocks/site";

export default function GuidePage() {
  const [active, setActive] = useState("全部");

  const list = useMemo(
    () => (active === "全部" ? guides : guides.filter((item) => item.category === active)),
    [active],
  );

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "求证指南",
      description: "面向普通用户的核验方法指南，涵盖信息溯源、证据判断、官方信息解读与传播伦理。",
      url: `${siteInfo.url}/guide`,
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: guides.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
          url: `${siteInfo.url}/guide/${item.slug}`,
        })),
      },
    },
  ];

  return (
    <>
      <Seo
        title="求证指南｜社会爆料核验方法与步骤 - 棱镜求证"
        description="从找源头、对时间到查回应，把核验社会爆料的过程拆成可操作的步骤，附证据判断、官方信息解读与传播伦理方法。"
        keywords="社会爆料求证,核验方法,辟谣技巧,信息溯源,真相核验"
        path="/guide"
        jsonLd={jsonLd}
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "求证指南", path: "/guide" }]} />
          <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            求证指南
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            核验并不神秘，它更像一套可以重复使用的习惯动作。这些指南把方法拆成具体步骤，
            读完就能用在你在群里看到的下一条信息上。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
          <div className="mb-8 flex flex-wrap gap-1.5">
            {guideCategories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setActive(item)}
                className={`cursor-pointer rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  active === item
                    ? "bg-accent-500 text-foreground-950"
                    : "bg-background-100 text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {list.map((item) => (
              <article
                key={item.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-accent-400"
              >
                <Link to={`/guide/${item.slug}`} className="block w-full" aria-label={item.title}>
                  <div className="h-44 w-full overflow-hidden bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.coverAlt}
                      title={`${item.title} - 求证指南`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </Link>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center justify-between gap-2">
                    <Tag tone="accent" icon="ri-bookmark-line">
                      {item.category}
                    </Tag>
                    <span className="text-xs text-foreground-500">{item.readTime}</span>
                  </div>
                  <h2 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-accent-800">
                    <Link to={`/guide/${item.slug}`}>{item.title}</Link>
                  </h2>
                  <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
                    {item.excerpt}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-1.5 border-t border-background-200 pt-3">
                    {item.tags.map((tag) => (
                      <span key={tag} className="rounded bg-background-100 px-2 py-0.5 text-[11px] text-foreground-500">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}